﻿// src/cockpit4/components/AICiteSourcePanel.tsx
// AI 引用来源 — 接入 /ai-visibility/cite-details 真实数据

import { useState, useEffect } from "react";
import {
  Download, Settings2, Search as SearchIcon,
  BarChart2, List, TrendingUp, ArrowUpDown, ChevronRight,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line, Legend,
} from "recharts";
import EmptyState, { MonitorFilterBar, SectionHeader } from "./_MonitorEmptyState";

interface Props {
  dateStart: string;
  dateEnd: string;
  onDateChange: (s: string, e: string) => void;
  onAddBrand: () => void;
}

const pieColors = ["#6366F1", "#22D3EE", "#34D399", "#F59E0B", "#EC4899", "#8B5CF6", "#06B6D4", "#EF4444", "#14B8A6", "#84CC16"];

interface CiteData {
  siteStats: { name: string; count: number }[];
  articleStats: { title: string; count: number }[];
  platformStats: { name: string; count: number }[];
  questionStats: { question: string; count: number }[];
  totalRecords: number;
}

export default function AICiteSourcePanel({ dateStart, dateEnd, onDateChange, onAddBrand }: Props) {
  const [tabMode, setTabMode] = useState<"ratio" | "trend">("ratio");
  const [tabTopMode, setTabTopMode] = useState<"list" | "chart" | "trend">("list");
  const [data, setData] = useState<CiteData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch("/ai-visibility/cite-details?days=7")
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setData({
            siteStats: d.siteStats || [],
            articleStats: d.articleStats || [],
            platformStats: d.platformStats || [],
            questionStats: d.questionStats || [],
            totalRecords: d.totalRecords || 0,
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateStart, dateEnd]);

  const siteStats = data?.siteStats || [];
  const articleStats = data?.articleStats || [];
  const platformStats = data?.platformStats || [];
  const questionStats = data?.questionStats || [];
  const totalRecords = data?.totalRecords || 0;
  const totalCiteCount = siteStats.reduce((s, x) => s + x.count, 0);
  const totalArticleCite = articleStats.reduce((s, x) => s + x.count, 0);
  const topSites = siteStats.slice(0, 40);

  return (
    <div className="min-h-full text-white">
      <div className="px-4 pt-4">
        <h2 className="text-[17px] font-bold text-white flex items-center gap-2">
          <span className="text-[14px]">📖</span> AI引用来源
        </h2>
      </div>

      <MonitorFilterBar onAddBrand={onAddBrand}
        dateStart={dateStart} dateEnd={dateEnd} onDateChange={onDateChange} />
      <div className="px-4">
        <div className="h-[7px] bg-gradient-to-r from-violet-500/30 via-cyan-500/20 to-violet-500/30 rounded-full mb-4" />

        {/* 数据概览 3 KPI */}
        <SectionHeader label="数据概览" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[
            { v: String(articleStats.length), sub: "引用文章数" },
            { v: String(totalCiteCount), sub: "引用次数" },
            { v: String(siteStats.length), sub: "引用站点" },
          ].map((k) => (
            <div key={k.sub} className="bg-gradient-to-br from-violet-500/10 via-indigo-500/8 to-cyan-500/10 rounded-2xl border border-violet-500/20 py-6 text-center">
              <div className="text-[40px] font-bold tracking-tight text-violet-400 leading-none">{k.v}</div>
              <div className="text-[13px] text-gray-500 mt-3">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* 引用信息 */}
        <SectionHeader label="引用信息" />

        {/* 第一行：占比 + 排行 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="bg-black/30 rounded-2xl border border-white/[0.08] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[14px] font-semibold text-gray-200">引用站点类型占比</div>
              <div className="flex rounded-lg bg-black/30 p-0.5">
                {(["ratio", "trend"] as const).map((t) => (
                  <button key={t} onClick={() => setTabMode(t)}
                    className={`flex items-center gap-1 px-3 py-1 rounded-md text-[11.5px] font-semibold transition-all ${
                      tabMode === t ? "bg-gradient-to-r from-violet-500 to-cyan-500 text-white shadow" : "text-gray-500 hover:text-gray-200"
                    }`}>
                    {t === "ratio" ? <>⊕ 占比</> : <>⎍ 趋势</>}
                  </button>
                ))}
              </div>
            </div>
            {tabMode === "ratio" ? (
              siteStats.length > 0 ? (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={siteStats.slice(0, 10)} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(entry: any) => entry.name}>
                        {siteStats.slice(0, 10).map((_: { name: string; count: number }, i: number) => (
                          <Cell key={i} fill={pieColors[i % pieColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState />
              )
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={siteStats.slice(0, 10).map((s, i) => ({ date: s.name, v: s.count }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line dataKey="v" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="bg-black/30 rounded-2xl border border-white/[0.08] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[14px] font-semibold text-gray-200">引用站点类型排行</div>
              <button className="text-[12px] text-violet-400 font-medium hover:text-violet-300 flex items-center">
                查看全部引用排行 <ChevronRight size={14} />
              </button>
            </div>
            {siteStats.length > 0 ? (
              <div className="space-y-2">
                {siteStats.slice(0, 8).map((s, i) => (
                  <div key={s.name} className="flex items-center gap-3">
                    <span className="text-[11px] text-gray-500 w-5">{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[12px] text-gray-200">{s.name}</span>
                        <span className="text-[12px] text-violet-400 font-semibold">{s.count}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-black/30 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500"
                          style={{ width: `${totalCiteCount > 0 ? (s.count / totalCiteCount * 100) : 0}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState />
            )}
          </div>
        </div>

        {/* 网站引用对比TOP40 */}
        <div className="bg-black/30 rounded-2xl border border-white/[0.08] p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[14px] font-semibold text-gray-200">网站引用对比TOP{Math.min(40, topSites.length)}</div>
            <div className="flex rounded-lg bg-black/30 p-0.5">
              {([
                { k: "list", t: "列表", I: List },
                { k: "chart", t: "图表", I: BarChart2 },
                { k: "trend", t: "趋势", I: TrendingUp },
              ] as const).map(({ k, t, I }) => (
                <button key={k} onClick={() => setTabTopMode(k)}
                  className={`flex items-center gap-1 px-3 py-1 rounded-md text-[11.5px] font-semibold transition-all ${
                    tabTopMode === k ? "bg-gradient-to-r from-violet-500 to-cyan-500 text-white shadow" : "text-gray-500 hover:text-gray-200"
                  }`}>
                  <I size={12} /> {t}
                </button>
              ))}
            </div>
          </div>
          {topSites.length === 0 ? (
            <EmptyState text="暂无引用站点" />
          ) : tabTopMode === "list" ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px] border-collapse">
                <thead>
                  <tr className="text-gray-400 text-[11.5px] font-semibold">
                    <th className="text-left p-2 bg-white/[0.04] rounded-l-lg">站点名称</th>
                    <th className="text-left p-2 bg-white/[0.04]">站点域名</th>
                    <th className="text-left p-2 bg-white/[0.04]">站点类型</th>
                    <th className="text-right p-2 bg-white/[0.04]">引用次数 <ArrowUpDown size={10} className="inline" /></th>
                    <th className="text-right p-2 bg-white/[0.04]">引用率 <ArrowUpDown size={10} className="inline" /></th>
                    <th className="text-right p-2 bg-white/[0.04] rounded-r-lg">占比 <ArrowUpDown size={10} className="inline" /></th>
                  </tr>
                </thead>
                <tbody>
                  {topSites.map((s, i) => (
                    <tr key={i} className="border-t border-white/[0.04] hover:bg-white/[0.02]">
                      <td className="p-2 text-gray-200">{s.name}</td>
                      <td className="p-2 text-gray-400">{s.name}</td>
                      <td className="p-2 text-gray-400">—</td>
                      <td className="p-2 text-right text-violet-400 font-semibold">{s.count}</td>
                      <td className="p-2 text-right text-gray-300">{totalRecords > 0 ? Math.round(s.count / totalRecords * 100) : 0}%</td>
                      <td className="p-2 text-right text-gray-300">{totalCiteCount > 0 ? Math.round(s.count / totalCiteCount * 100) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : tabTopMode === "chart" ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topSites.slice(0, 15)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366F1" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={topSites.map((s) => ({ date: s.name, v: s.count }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="v" stroke="#6366F1" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* 引用平台 + 引用AI问题 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="bg-black/30 rounded-2xl border border-white/[0.08] p-5">
            <div className="text-[14px] font-semibold text-gray-200 mb-3">引用平台</div>
            {platformStats.length > 0 ? (
              <div className="space-y-2">
                {platformStats.map((p, i) => (
                  <div key={p.name} className="flex items-center gap-3">
                    <span className="text-[11px] text-gray-500 w-5">{i + 1}</span>
                    <span className="text-[12px] text-gray-200 w-20">{p.name}</span>
                    <div className="flex-1 h-2 rounded-full bg-black/30 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500"
                        style={{ width: `${totalRecords > 0 ? (p.count / totalRecords * 100) : 0}%` }} />
                    </div>
                    <span className="text-[12px] text-violet-400 font-semibold w-8 text-right">{p.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState />
            )}
          </div>
          <div className="bg-black/30 rounded-2xl border border-white/[0.08] p-5">
            <div className="text-[14px] font-semibold text-gray-200 mb-3">引用AI问题</div>
            {questionStats.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {questionStats.slice(0, 10).map((q, i) => (
                  <div key={i} className="flex items-start gap-2 text-[12px]">
                    <span className="text-violet-400 font-semibold shrink-0">{q.count}</span>
                    <span className="text-gray-300 line-clamp-2">{q.question}</span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState />
            )}
          </div>
        </div>

        {/* 引用文章 */}
        <div className="bg-black/30 rounded-2xl border border-white/[0.08] p-5 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-[14px] font-semibold text-gray-200">引用文章</div>
            <div className="flex items-center gap-2 flex-wrap">
              <button className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px]
                                 bg-gradient-to-r from-violet-500 to-cyan-500 text-white font-semibold hover:bg-violet-600">
                <Download size={13} /> 导出
              </button>
              <button className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px]
                                 bg-black/30 border border-white/[0.08] text-white font-semibold hover:text-violet-300 hover:border-violet-400/40">
                <Settings2 size={13} /> 列表配置
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px] border-collapse">
              <thead>
                <tr className="bg-black/30 text-gray-400 text-[11.5px]">
                  <th className="text-left p-3 rounded-l-lg">文章标题</th>
                  <th className="text-left p-3">发布站点</th>
                  <th className="text-left p-3">站点类型</th>
                  <th className="text-right p-3">引用次数 <ArrowUpDown size={10} className="inline text-violet-400" /></th>
                  <th className="text-right p-3 rounded-r-lg">引用率</th>
                </tr>
              </thead>
              <tbody>
                {articleStats.length > 0 ? (
                  articleStats.map((a, i) => (
                    <tr key={i} className="border-t border-white/[0.04] hover:bg-white/[0.02]">
                      <td className="p-3 text-gray-200 max-w-xs truncate">{a.title}</td>
                      <td className="p-3 text-gray-400">官网</td>
                      <td className="p-3 text-gray-400">企业官网</td>
                      <td className="p-3 text-right text-violet-400 font-semibold">{a.count}</td>
                      <td className="p-3 text-right text-gray-300">{totalRecords > 0 ? Math.round(a.count / totalRecords * 100) : 0}%</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={5}><EmptyState text="暂无引用文章" /></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
